import { Box, Button, Chip, CircularProgress, IconButton, Stack, Typography } from "@mui/material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import DeleteIcon from "@mui/icons-material/Delete";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SummarizeIcon from "@mui/icons-material/Summarize";
import VisibilityIcon from "@mui/icons-material/Visibility";

const MONTHS = ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"];

function splitDate(value) {
  if (!value) return { day: "--", monthYear: "--/--" };
  const [year, month, day] = String(value).split("T")[0].split("-");
  return {
    day: day || "--",
    monthYear: `${MONTHS[Number(month) - 1] || month || "--"}/${String(year || "--").slice(-2)}`,
  };
}

function formatDate(value) {
  if (!value) return "-";
  const [year, month, day] = String(value).split("T")[0].split("-");
  return `${day}/${month}/${year}`;
}

function formatMoney(value) {
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function DateBadge({ date }) {
  const parsed = splitDate(date);

  return (
    <Box sx={{ textAlign: "center", minWidth: 58 }}>
      <Box
        sx={{
          px: 0.9,
          height: 34,
          borderRadius: "8px",
          bgcolor: "secondary.100",
          color: "primary.900",
          fontSize: 26,
          fontWeight: 700,
          lineHeight: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {parsed.day}
      </Box>
      <Typography sx={{ mt: 0.2, fontSize: 11, fontWeight: 700, color: "neutral.600" }}>
        {parsed.monthYear}
      </Typography>
    </Box>
  );
}

function MoneyStat({ label, value, emphasize }) {
  return (
    <Box sx={{ minWidth: { xs: "auto", md: 88 } }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: "neutral.600" }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 800,
          color: emphasize ? "primary.900" : "rgba(18, 27, 61, 0.86)",
        }}
      >
        {formatMoney(value)}
      </Typography>
    </Box>
  );
}

export default function CierreCard({
  closure,
  variant,
  deleting,
  downloadingPdf,
  showPdfAction = false,
  onContinue,
  onDelete,
  onViewMovements,
  onViewCash,
  onViewSummary,
  onViewReport,
  onDownloadPdf,
}) {
  const isDraft = variant === "draft";

  return (
    <Box
      sx={{
        p: { xs: 1.5, md: 1.3 },
        bgcolor: "neutral.50",
        borderRadius: "14px",
        border: "1px solid rgba(255,255,255,0.9)",
        boxShadow: "0px 6px 16px rgba(27, 37, 89, 0.08)",
        display: "grid",
        gridTemplateColumns: { xs: "1fr", lg: "190px 1fr auto" },
        gap: { xs: 1.5, lg: 2 },
        alignItems: "center",
      }}
    >
      <Stack direction="row" spacing={1.2} alignItems="center">
        <DateBadge date={closure.periodStart} />
        <Typography sx={{ fontSize: 28, fontWeight: 800, color: "primary.900" }}>-</Typography>
        <DateBadge date={closure.periodEnd} />
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            md: "1.2fr repeat(4, minmax(0, 1fr))",
          },
          gap: 1.5,
          alignItems: "center",
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "neutral.600" }}>
            Fecha de generación
          </Typography>
          <Typography sx={{ fontSize: 12.5, fontWeight: 900, color: "primary.900" }}>
            {formatDate(closure.generatedAt)}
          </Typography>
        </Box>

        <MoneyStat label="Ingresos" value={closure.totalIncome} />
        <MoneyStat label="Egresos" value={closure.totalExpense} />
        <MoneyStat label="Saldo" value={closure.balance} emphasize />

        <Chip
          label={isDraft ? "En trabajo" : "Completado"}
          size="small"
          sx={{
            width: "fit-content",
            borderRadius: "999px",
            bgcolor: isDraft ? "secondary.100" : "rgba(185, 246, 202, 0.72)",
            color: "primary.900",
            fontSize: 11,
            fontWeight: 900,
          }}
        />
      </Box>

      <Stack
        direction="row"
        spacing={0.8}
        justifyContent={{ xs: "flex-end", lg: "flex-start" }}
        sx={{ flexWrap: "wrap", rowGap: 1 }}
      >
        {isDraft ? (
          <>
            <Button
              onClick={() => onContinue(closure)}
              startIcon={<PlayArrowIcon />}
              sx={{
                bgcolor: "primary.900",
                color: "white",
                px: 1.8,
                py: 0.7,
                borderRadius: "24px",
                border: "2px solid",
                borderColor: "primary.900",
                textTransform: "none",
                fontWeight: 800,
                fontSize: 13,
                "&:hover": { bgcolor: "primary.800" },
              }}
            >
              Continuar
            </Button>

            {onViewSummary ? (
              <Button
                onClick={() => onViewSummary(closure)}
                startIcon={<SummarizeIcon />}
                sx={{
                  bgcolor: "white",
                  color: "primary.900",
                  px: 1.8,
                  py: 0.7,
                  borderRadius: "24px",
                  border: "2px solid",
                  borderColor: "primary.900",
                  textTransform: "none",
                  fontWeight: 800,
                  fontSize: 13,
                  "&:hover": { bgcolor: "neutral.100" },
                }}
              >
                Resumen
              </Button>
            ) : null}

            <IconButton
              onClick={() => onDelete(closure)}
              disabled={deleting}
              title="Eliminar cierre en trabajo"
              sx={{
                width: 38,
                height: 38,
                bgcolor: "error.50",
                color: "error.700",
                border: "1px solid",
                borderColor: "error.200",
                "&:hover": { bgcolor: "error.100" },
              }}
            >
              {deleting ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
            </IconButton>
          </>
        ) : (
          <>
            {onViewReport ? (
              <Button
                onClick={() => onViewReport(closure)}
                startIcon={<VisibilityIcon />}
                sx={{
                  bgcolor: "white",
                  color: "primary.900",
                  px: 1.8,
                  py: 0.7,
                  borderRadius: "24px",
                  border: "2px solid",
                  borderColor: "primary.900",
                  textTransform: "none",
                  fontWeight: 800,
                  fontSize: 13,
                  "&:hover": { bgcolor: "neutral.100" },
                }}
              >
                Ver reporte
              </Button>
            ) : (
              <>
                {onViewSummary ? (
                  <Button
                    onClick={() => onViewSummary(closure)}
                    startIcon={<SummarizeIcon />}
                    sx={{
                      bgcolor: "white",
                      color: "primary.900",
                      px: 1.8,
                      py: 0.7,
                      borderRadius: "24px",
                      border: "2px solid",
                      borderColor: "primary.900",
                      textTransform: "none",
                      fontWeight: 800,
                      fontSize: 13,
                      "&:hover": { bgcolor: "neutral.100" },
                    }}
                  >
                    Resumen
                  </Button>
                ) : null}

                <Button
                  onClick={() => onViewMovements(closure)}
                  startIcon={<VisibilityIcon />}
                  sx={{
                    bgcolor: "white",
                    color: "primary.900",
                    px: 1.8,
                    py: 0.7,
                    borderRadius: "24px",
                    border: "2px solid",
                    borderColor: "primary.900",
                    textTransform: "none",
                    fontWeight: 800,
                    fontSize: 13,
                    "&:hover": { bgcolor: "neutral.100" },
                  }}
                >
                  Movimientos
                </Button>

                <Button
                  onClick={() => onViewCash(closure)}
                  startIcon={<AccountBalanceWalletIcon />}
                  sx={{
                    bgcolor: "primary.900",
                    color: "white",
                    px: 1.8,
                    py: 0.7,
                    borderRadius: "24px",
                    border: "2px solid",
                    borderColor: "primary.900",
                    textTransform: "none",
                    fontWeight: 800,
                    fontSize: 13,
                    "&:hover": { bgcolor: "primary.800" },
                  }}
                >
                  Caja
                </Button>
              </>
            )}

            {showPdfAction ? (
              <IconButton
                onClick={() => onDownloadPdf?.(closure)}
                disabled={downloadingPdf}
                title="Descargar reporte PDF"
                sx={{
                  bgcolor: "#ffebee",
                  color: "#c62828",
                  border: "1px solid #ffcdd2",
                  "&:hover": { bgcolor: "#ffcdd2" },
                }}
              >
                {downloadingPdf ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <PictureAsPdfIcon />
                )}
              </IconButton>
            ) : null}
          </>
        )}
      </Stack>
    </Box>
  );
}
